import envDev from './env.dev';

const isDev = process.env.NODE_ENV === 'development';

const WINDOW_ENV = (() => {
    if (typeof window === 'undefined') return {} as Record<string, string | undefined>;
    return (window as typeof window & { __ENV__?: Record<string, string | undefined> }).__ENV__ || {};
})();

const pick = (keys: string[], fallback = ''): string => {
    for (const key of keys) {
        const fromWindow = WINDOW_ENV[key];
        if (typeof fromWindow === 'string' && fromWindow.trim()) {
            return fromWindow.trim();
        }

        const fromProcess = process.env[key];
        if (typeof fromProcess === 'string' && fromProcess.trim()) {
            return fromProcess.trim();
        }
    }

    return fallback;
};

const cleanBaseUrl = (value: string) => value.replace(/\/$/, '');

/**
 * Origin engine AI Hub (không có `/api`). Rỗng → FE gọi `/api/v1/aihub` qua API gateway (vd. internal.toppion.com.vn).
 * Deploy: set `CONFIG_NEXT_PUBLIC_AIHUB_API_BASE_URL` (container / env.js), vd. `http://103.130.213.124:8001`.
 * Runtime đọc lại qua `resolveAiHubApiOrigin()` — không chỉ dựa vào object bake lúc build.
 */
const resolveAiHubOrigin = (): string => {
    const raw = pick(
        [
            'AIHUB_BASE_URL',
            'NEXT_PUBLIC_AIHUB_API_ORIGIN',
            'CONFIG_NEXT_PUBLIC_AIHUB_API_BASE_URL',
        ],
        process.env.CONFIG_NEXT_PUBLIC_AIHUB_API_BASE_URL || 'https://staging-api.toppion.com.vn'
    );
    if (!raw) {
        return '';
    }
    return cleanBaseUrl(raw.replace(/\/api$/i, ''));
};

const AIHUB_ORIGIN = resolveAiHubOrigin();
const AIHUB_API_HOST = AIHUB_ORIGIN ? `${AIHUB_ORIGIN}/api` : '';

const buildProductionEnvironment = () => {
    const BASE_URL = pick(
        ['BASE_URL', 'CONFIG_NEXT_PUBLIC_API_BASE_URL', 'NEXT_PUBLIC_API_BASE_URL', 'API_BASE_URL'],
        process.env.CONFIG_NEXT_PUBLIC_API_BASE_URL || 'https://internal.toppion.com.vn'
    );

    const CULTURE_BASE_URL = pick(
        [
            'CULTURE_BASE_URL',
            'CONFIG_NEXT_PUBLIC_CULTURE_API_BASE_URL',
            'NEXT_PUBLIC_CULTURE_API_BASE_URL',
            'NEXT_PUBLIC_CULTURE_API_ORIGIN',
            'CULTURE_API_BASE_URL',
        ],
        process.env.CONFIG_NEXT_PUBLIC_CULTURE_API_BASE_URL || 'https://internal.toppion.com.vn'
    );

    const ELEARNING_URL = pick(
        ['CONFIG_NEXT_PUBLIC_ELEARNING_URL', 'NEXT_PUBLIC_ELEARNING_URL', 'ELEARNING_URL'],
        process.env.CONFIG_NEXT_PUBLIC_ELEARNING_URL || 'https://elearning.toppion.com.vn'
    );

    // Linux inject NEXT_PUBLIC_FINDER_URL; Windows/product thường dùng CONFIG_NEXT_PUBLIC_FINDER_URL.
    // Không lấy FILE_MANAGER từ CONFIG_NEXT_PUBLIC_CULTURE_API_BASE_URL (service-linux/prodservices → 404 widget).
    const FINDER_URL = pick(
        [
            'NEXT_PUBLIC_FINDER_URL',
            'CONFIG_NEXT_PUBLIC_FINDER_URL',
            'CONFIG_NEXT_FINDER_URL',
            'NEXT_FINDER_URL',
            'FINDER_URL',
        ],
        process.env.NEXT_PUBLIC_FINDER_URL ||
            process.env.CONFIG_NEXT_PUBLIC_FINDER_URL ||
            'https://finder-topkpi.toppion.com.vn'
    );

    return {
        FILE_MANAGER: cleanBaseUrl(FINDER_URL),
        ELEARNING_HOST: cleanBaseUrl(ELEARNING_URL),
        API_HOST: `${cleanBaseUrl(BASE_URL)}/api`,
        CULTURE_API_HOST: `${cleanBaseUrl(CULTURE_BASE_URL)}/api`,
        API_HOST_DEV: '/api',
        BASE_URL: cleanBaseUrl(BASE_URL),
        CULTURE_BASE_URL: cleanBaseUrl(CULTURE_BASE_URL),
        AIHUB_BASE_URL: AIHUB_ORIGIN,
        AIHUB_API_HOST,
    };
};

/** Dev: dùng env.dev.ts. Production: đọc từ env variables / window.__ENV__. */
const ENVIRONMENT = isDev
    ? {
          FILE_MANAGER: cleanBaseUrl(envDev.FILE_MANAGER || envDev.BASE_URL || ''),
          ELEARNING_HOST: cleanBaseUrl(envDev.ELEARNING_HOST || ''),
          API_HOST: cleanBaseUrl(envDev.API_HOST || `${envDev.BASE_URL || ''}/api`),
          CULTURE_API_HOST: cleanBaseUrl(
              envDev.CULTURE_API_HOST || envDev.API_HOST || `${envDev.BASE_URL || ''}/api`
          ),
          API_HOST_DEV: envDev.API_HOST_DEV || '/api',
          BASE_URL: cleanBaseUrl(envDev.BASE_URL || ''),
          CULTURE_BASE_URL: cleanBaseUrl(envDev.CULTURE_BASE_URL || envDev.BASE_URL || ''),
          AIHUB_BASE_URL: AIHUB_ORIGIN || cleanBaseUrl(String(envDev.AIHUB_BASE_URL || '')),
          AIHUB_API_HOST: AIHUB_API_HOST || (envDev.AIHUB_API_HOST ? cleanBaseUrl(String(envDev.AIHUB_API_HOST)) : ''),
      }
    : buildProductionEnvironment();

export default ENVIRONMENT;
